import React, { useState } from "react";
import { useStripe } from "@stripe/react-stripe-js";
import { toast } from "react-toastify";
import { useTheme } from "../../../context/ThemeContext";
import {
  getSecondaryText,
  getLightBorder,
  getPrimaryText,
} from "../../../utils/themeUtils";
import {
  ACH_PROCESSING_MESSAGE,
  getACHMandateText,
} from "../../../types/payment.types";

interface BankAccountElementProps {
  clientSecret: string;
  stripeCustomerId: string;
  memberName: string;
  businessName?: string;
  onSuccess: (paymentMethodId: string) => void;
  onError?: (error: string) => void;
  onCancel?: () => void;
  disabled?: boolean;
}

/**
 * BankAccountElement - Component for linking a US bank account via Stripe Financial Connections
 *
 * Uses Stripe's collectBankAccountForSetup to initiate the Financial Connections flow,
 * which provides instant verification when possible. Falls back to micro-deposits if needed.
 */
const BankAccountElement: React.FC<BankAccountElementProps> = ({
  clientSecret,
  stripeCustomerId,
  memberName,
  businessName = "Club Central",
  onSuccess,
  onError,
  onCancel,
  disabled = false,
}) => {
  const { isDarkMode } = useTheme();
  const stripe = useStripe();
  const [loading, setLoading] = useState(false);

  const handleLinkBankAccount = async () => {
    if (!stripe || !clientSecret) {
      toast.error("Payment system not initialized. Please refresh and try again.");
      return;
    }

    setLoading(true);

    try {
      // Use Financial Connections to collect bank account
      const result = await stripe.collectBankAccountForSetup({
        clientSecret,
        params: {
          payment_method_type: "us_bank_account",
          payment_method_data: {
            billing_details: {
              name: memberName,
            },
          },
        },
      });

      if (result.error) {
        // User cancelled or error occurred
        if (result.error.type === "validation_error") {
          toast.error("Please complete all required fields.");
        } else if (result.error.code === "user_canceled") {
          onCancel?.();
        } else {
          toast.error(result.error.message || "Failed to link bank account");
          onError?.(result.error.message || "Failed to link bank account");
        }
        setLoading(false);
        return;
      }

      const { setupIntent } = result;

      if (!setupIntent) {
        toast.error("Failed to complete bank account setup");
        setLoading(false);
        return;
      }

      // Check the status of the SetupIntent
      if (setupIntent.status === "succeeded") {
        // Bank account linked and verified instantly
        toast.success("Bank account linked successfully!");
        if (setupIntent.payment_method) {
          onSuccess(setupIntent.payment_method as string);
        }
      } else if (setupIntent.status === "requires_confirmation") {
        // Need to confirm the SetupIntent with mandate acceptance
        const confirmResult = await stripe.confirmUsBankAccountSetup(clientSecret);

        if (confirmResult.error) {
          toast.error(confirmResult.error.message || "Failed to confirm bank account");
          onError?.(confirmResult.error.message || "Failed to confirm bank account");
        } else if (confirmResult.setupIntent?.status === "succeeded") {
          toast.success("Bank account linked successfully!");
          if (confirmResult.setupIntent.payment_method) {
            onSuccess(confirmResult.setupIntent.payment_method as string);
          }
        } else if (confirmResult.setupIntent?.status === "requires_action") {
          // Micro-deposits required
          toast.info("Bank account added! Micro-deposits will be sent for verification. This may take 1-2 business days.");
          if (confirmResult.setupIntent.payment_method) {
            onSuccess(confirmResult.setupIntent.payment_method as string);
          }
        }
      } else if (setupIntent.status === "requires_action") {
        // Micro-deposits required for verification
        toast.info("Bank account added! Micro-deposits will be sent for verification. This may take 1-2 business days.");
        if (setupIntent.payment_method) {
          onSuccess(setupIntent.payment_method as string);
        }
      } else {
        // Unknown status
        toast.info("Bank account setup in progress...");
      }
    } catch (error: any) {
      toast.error(error.message || "An error occurred while linking bank account");
      onError?.(error.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* ACH Information */}
      <div
        className={`p-4 rounded-lg border ${getLightBorder(isDarkMode)} ${
          isDarkMode ? "bg-gray-800/50" : "bg-blue-50"
        }`}
      >
        <div className="flex items-start gap-3">
          <svg
            className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
              isDarkMode ? "text-blue-400" : "text-blue-600"
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
          <div className="flex-1">
            <h4
              className={`font-medium ${getPrimaryText(isDarkMode)} text-sm`}
            >
              Link Your Bank Account
            </h4>
            <p
              className={`mt-1 text-sm ${getSecondaryText(isDarkMode)}`}
            >
              Securely connect your bank account for ACH payments. This uses
              Stripe's secure bank linking system.
            </p>
          </div>
        </div>
      </div>

      {/* ACH Processing Time Notice */}
      <div
        className={`p-3 rounded-lg ${
          isDarkMode ? "bg-yellow-900/30" : "bg-yellow-50"
        } border ${isDarkMode ? "border-yellow-700" : "border-yellow-200"}`}
      >
        <p
          className={`text-sm ${
            isDarkMode ? "text-yellow-200" : "text-yellow-800"
          }`}
        >
          <strong>Note:</strong> {ACH_PROCESSING_MESSAGE}
        </p>
      </div>

      {/* Link Bank Account Button */}
      <button
        type="button"
        onClick={handleLinkBankAccount}
        disabled={disabled || loading || !stripe}
        className="w-full bg-[var(--cc-action)] text-[var(--cc-action-text)] hover:bg-[var(--cc-action-hover)] px-6 py-3 rounded-md font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <div
              className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: "#fff", borderTopColor: "transparent" }}
            />
            <span>Connecting...</span>
          </>
        ) : (
          <>
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            </svg>
            <span>Link Bank Account</span>
          </>
        )}
      </button>

      {/* Mandate Text */}
      <p className={`text-xs ${getSecondaryText(isDarkMode)} leading-relaxed`}>
        {getACHMandateText(businessName)}
      </p>
    </div>
  );
};

export default BankAccountElement;
